import MenuItem from "@mui/material/MenuItem";
import { IconButton } from "@mui/material";
import { MoreVertIcon } from "../images/MoreVertIcon";
import { PenIcon, TrashCanIcon } from "../images";
import { EyeIcon } from "../images/EyeIcon";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../types/enums";
import { ReactNode, useEffect, useState } from "react";
import {
  accountsApi,
  campaignsApi,
  useGetCampaignSummaryQuery,
} from "../../api";
import { useStateWithSearchParams, useAppDispatch } from "../../hooks";
import { StyledMenu } from "../ui";
import { DiscardAccountModal, EditAccountDetailsModal } from "../ui/modal";

enum MenuModals {
  discard = "discard",
  edit = "edit",
  none = "none",
}

type SearchParams = {
  page?: string;
};

export const CompanyMenu = ({
  id,
  companyName,
  total_companies,
  isDisabled,
  removeSelected,
  campaign_id,
  isEditor,
  websiteUrl,
}: {
  id: string;
  companyName: string;
  total_companies: number;
  isDisabled?: boolean;
  removeSelected: (id: string) => void;
  campaign_id: string;
  isEditor?: boolean;
  websiteUrl: string;
}) => {
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openModal, setOpenModal] = useState<MenuModals>(MenuModals.none);
  const [currentFlow, setCurrentFlow] = useState<string>("");
  const navigate = useNavigate();
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      page: undefined,
    },
  );

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id,
  });

  useEffect(() => {
    if (currentFlow) {
      setCurrentFlow("");
    }
  }, [currentFlow]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const detailsHandler = (id: string) => {
    navigate(`${AppRoutes.ACCOUNT}/${campaign_id}/${id}`);
  };

  const onSuccessDiscardHandler = () => {
    removeSelected(id);
    if (searchDict.page && total_companies === 1) {
      setSearchValue("page", undefined);
    }
    dispatch(accountsApi.util.invalidateTags(["analytics"]));
    dispatch(
      campaignsApi.util.invalidateTags(["summary", "filters", "activities"]),
    );
    setOpenModal(MenuModals.none);
    setAnchorEl(null);
  };

  const onCloseHandler = () => {
    setOpenModal(MenuModals.none);
    setAnchorEl(null);
  };

  const options = [
    {
      label: "Details",
      icon: <EyeIcon />,
      onClick: detailsHandler,
    },
    {
      label: "Edit Account Details",
      icon: <PenIcon color={"#585858"} />,
      onClick: () => setOpenModal(MenuModals.edit),
      disabled: summary?.is_demo,
    },
    {
      label: "Discard",
      icon: <TrashCanIcon />,
      onClick: () => setOpenModal(MenuModals.discard),
      disabled: summary?.is_demo,
    },
  ];

  const modal: Record<MenuModals, ReactNode> = {
    [MenuModals.discard]: (
      <DiscardAccountModal
        account_id={id}
        onClose={onCloseHandler}
        companyName={companyName}
        onSuccessHandler={onSuccessDiscardHandler}
        campaign_id={campaign_id}
      />
    ),
    [MenuModals.edit]: (
      <EditAccountDetailsModal
        account_id={id}
        campaign_id={campaign_id}
        accountWebsite={websiteUrl}
        accountName={companyName}
        onClose={onCloseHandler}
      />
    ),
    [MenuModals.none]: null,
  };

  return (
    <div>
      <IconButton
        disabled={isDisabled || !isEditor}
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon width="24" />
      </IconButton>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "white",
          },
        }}
      >
        {options.map(({ label, onClick, icon, disabled }) => (
          <MenuItem
            disabled={disabled}
            key={label}
            sx={{ display: "flex", gap: "12px", alignItems: "center" }}
            onClick={() => onClick(id)}
          >
            {icon}
            {label}
          </MenuItem>
        ))}
      </StyledMenu>

      {modal[openModal]}
    </div>
  );
};
